import { useEffect, useRef, useState } from 'react';

/** @type {{ flipUp: boolean, alignRight: boolean }} */
const CENTERED = Object.freeze({ flipUp: false, alignRight: false });

/**
 * Decide which side of the input the dropdown should open on. The list can
 * only flip when it would otherwise cross the viewport edge AND there is more
 * room on the flipped side; ties and unmeasurable (jsdom) rects keep defaults.
 * @param {DOMRect} inputRect
 * @param {{ width: number, height: number }} listSize
 * @returns {{ flipUp: boolean, alignRight: boolean }}
 */
export function computePlacement(inputRect, listSize) {
	const gap = 4;
	const fitsBelow = window.innerHeight - inputRect.bottom >= listSize.height + gap;
	const fitsAbove = inputRect.top >= listSize.height + gap;
	const flipUp = !fitsBelow && (fitsAbove || inputRect.top > window.innerHeight - inputRect.bottom);
	const fitsRight = window.innerWidth - inputRect.right >= listSize.width;
	const fitsLeft = inputRect.left >= listSize.width;
	const alignRight = !fitsRight && (fitsLeft || inputRect.left > window.innerWidth - inputRect.right);
	return { flipUp, alignRight };
}

/**
 * Shared listbox UI state and keyboard navigation used by Combobox and TagPicker.
 *
 * @param {Object} params
 * @param {string} params.idPrefix - base id for option/listbox ARIA ids
 * @param {boolean} params.open
 * @param {(v: boolean) => void} params.setOpen
 * @param {number} params.suggestionCount
 * @param {(highlighted: number, e: import('react').KeyboardEvent<HTMLInputElement>) => void} [params.onEnter]
 * @returns {{ rootRef: import('react').RefObject<HTMLDivElement | null>, listRef: import('react').RefObject<HTMLElement | null>, highlighted: number, setHighlighted: import('react').Dispatch<import('react').SetStateAction<number>>, clampHighlight: number, showSuggestions: boolean, flipUp: boolean, alignRight: boolean, handleKeyDown: (e: import('react').KeyboardEvent<HTMLInputElement>) => boolean }}
 */
export function usePickerList({ idPrefix, open, setOpen, suggestionCount, onEnter }) {
	const rootRef = useRef(/** @type {HTMLDivElement | null} */ (null));
	const listRef = useRef(/** @type {HTMLElement | null} */ (null));
	const [highlighted, setHighlighted] = useState(0);
	const [placement, setPlacement] = useState(CENTERED);

	const clampHighlight = Math.min(highlighted, Math.max(suggestionCount - 1, 0));
	const showSuggestions = open && suggestionCount > 0;

	useEffect(() => {
		/** @param {MouseEvent} e */
		function onOutsideMouseDown(e) {
			if (rootRef.current && !rootRef.current.contains(e.target)) { setOpen(false); }
		}
		document.addEventListener('mousedown', onOutsideMouseDown);
		return () => document.removeEventListener('mousedown', onOutsideMouseDown);
	}, [setOpen]);

	// Reposition the open dropdown when it appears or the viewport changes.
	// Only the input rect and list size feed the decision, never the list's
	// own rendered position, so applying the flipped classes cannot re-trigger.
	useEffect(() => {
		const input = rootRef.current?.querySelector('input');
		const list = listRef.current;
		if (!showSuggestions || !input || !list) { return () => {}; }

		/** @returns {void} */
		function measure() {
			setPlacement(computePlacement(input.getBoundingClientRect(), {
				width: list.offsetWidth,
				height: list.offsetHeight,
			}));
		}

		measure();
		window.addEventListener('resize', measure);
		return () => window.removeEventListener('resize', measure);
	}, [showSuggestions, suggestionCount]);

	useEffect(() => {
		if (!showSuggestions) { return; }
		const el = listRef.current?.querySelector(
			`#${idPrefix}-option-${clampHighlight}`
		);
		el?.scrollIntoView({ block: 'nearest' });
	}, [clampHighlight, showSuggestions, idPrefix]);

	/**
	 * Jump to the first or last item.
	 * @param {boolean} start — true to jump to the first item, false for last
	 */
	function jump(start) {
		if (suggestionCount === 0) { return; }
		setHighlighted(start ? 0 : Math.max(suggestionCount - 1, 0));
		setOpen(true);
	}

	/**
	 * Unified keyboard handler for arrow, enter, escape, and modifier jumps.
	 * Returns true when the key was consumed.
	 * @param {import('react').KeyboardEvent<HTMLInputElement>} e
	 * @returns {boolean}
	 */
	function handleKeyDown(e) {
		if ((e.metaKey || e.ctrlKey) && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
			e.preventDefault();
			jump(e.key === 'ArrowUp');
			return true;
		}
		const arrowDir = e.key === 'ArrowDown' ? 1 : (e.key === 'ArrowUp' ? -1 : 0);
		if (arrowDir !== 0 && suggestionCount > 0) {
			e.preventDefault();
			setHighlighted((h) => (h + arrowDir + suggestionCount) % suggestionCount);
			setOpen(true);
			return true;
		}
		if (e.key === 'Enter') {
			if (!open) { return false; }
			e.preventDefault();
			e.stopPropagation();
			onEnter?.(clampHighlight, e);
			return true;
		}
		if (e.key === 'Escape') { setOpen(false); return true; }
		return false;
	}

	return {
		rootRef, listRef, highlighted, setHighlighted,
		clampHighlight, showSuggestions, flipUp: placement.flipUp,
		alignRight: placement.alignRight, handleKeyDown,
	};
}
